import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { 
  LayoutDashboard, Users, BookOpen, UserCog, Video,
  TrendingUp, Activity, BarChart3, PieChart, Award,
  Gamepad2, FileText, Clock, Flame, ArrowUp, ArrowDown,
  ChevronRight, Calendar, Target
} from 'lucide-react';

const AdminAnalytics = () => {
  const { user, api } = useAuth();
  const [overview, setOverview] = useState(null);
  const [trends, setTrends] = useState(null);
  const [subjects, setSubjects] = useState(null);
  const [topStudents, setTopStudents] = useState(null);
  const [games, setGames] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(30);

  const sidebarItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'User Management', icon: Users },
    { path: '/admin/allocate', label: 'Subject Allocation', icon: BookOpen },
    { path: '/admin/tutors', label: 'Tutor Assignment', icon: UserCog },
    { path: '/tutoring', label: 'Live Sessions', icon: Video },
    { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  ];

  useEffect(() => {
    fetchAnalytics();
  }, [selectedPeriod]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [overviewRes, trendsRes, subjectsRes, studentsRes, gamesRes] = await Promise.all([
        api.get('/v2/analytics/admin/overview'),
        api.get(`/v2/analytics/admin/trends?days=${selectedPeriod}`),
        api.get('/v2/analytics/admin/subject-performance'),
        api.get('/v2/analytics/admin/top-students?limit=10'),
        api.get('/v2/analytics/admin/game-analytics')
      ]);
      
      setOverview(overviewRes.data);
      setTrends(trendsRes.data);
      setSubjects(subjectsRes.data);
      setTopStudents(studentsRes.data);
      setGames(gamesRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-3xl font-bold text-slate-800 mt-1">{value}</p>
            {subtitle && (
              <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            )}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${trend >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                <span>{Math.abs(trend)}% this week</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Simple bar chart component
  const SimpleBarChart = ({ data, valueKey, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0)) || 1;
    return (
      <div className="flex items-end gap-1 h-32">
        {data.slice(-14).map((d, i) => (
          <div 
            key={i}
            className="flex-1 bg-emerald-500 rounded-t transition-all hover:bg-emerald-600"
            style={{ height: `${(d[valueKey] / max) * 100}%`, minHeight: d[valueKey] > 0 ? '4px' : '0' }}
            title={`${d.date}: ${d[valueKey]}`}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <ProLayout title="Analytics" sidebarItems={sidebarItems} user={user}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </ProLayout>
    );
  }

  return (
    <ProLayout title="Analytics Dashboard" sidebarItems={sidebarItems} user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Platform Analytics</h1>
            <p className="text-slate-500">Monitor platform performance and student engagement</p>
          </div>
          <div className="flex gap-2">
            {[7, 14, 30, 60].map(days => (
              <Button
                key={days}
                variant={selectedPeriod === days ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedPeriod(days)}
              >
                {days}d
              </Button>
            ))}
          </div>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={overview?.users?.total || 0}
            icon={Users}
            color="bg-blue-500"
            subtitle={`${overview?.users?.new_this_week || 0} new this week`}
          />
          <StatCard
            title="Active Students"
            value={overview?.activity?.active_students_this_week || 0}
            icon={Activity}
            color="bg-emerald-500"
            subtitle={`${overview?.activity?.engagement_rate || 0}% engagement`}
          />
          <StatCard
            title="Lessons Completed"
            value={overview?.activity?.total_lessons_completed || 0}
            icon={BookOpen}
            color="bg-purple-500"
            subtitle={`${overview?.activity?.lessons_this_week || 0} this week`}
          />
          <StatCard
            title="Games Played"
            value={overview?.activity?.total_games_played || 0}
            icon={Gamepad2}
            color="bg-amber-500"
            subtitle={`${overview?.activity?.games_this_week || 0} this week`}
          />
        </div>

        {/* User Breakdown */}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-500" />
                Users by Role
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(overview?.users?.by_role || {}).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="capitalize text-slate-600">{role}s</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            role === 'student' ? 'bg-emerald-500' :
                            role === 'parent' ? 'bg-blue-500' :
                            role === 'tutor' ? 'bg-purple-500' : 'bg-amber-500'
                          }`}
                          style={{ width: `${(count / (overview?.users?.total || 1)) * 100}%` }}
                        />
                      </div>
                      <span className="font-semibold text-slate-800 w-8">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Activity Trend */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Lesson Activity (Last {selectedPeriod} Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {trends?.lessons && <SimpleBarChart data={trends.lessons} valueKey="count" />}
              <div className="flex justify-between mt-2 text-xs text-slate-400">
                <span>{trends?.lessons?.[0]?.date}</span>
                <span>{trends?.lessons?.[trends.lessons.length - 1]?.date}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subject Performance & Top Students */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Subject Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Target className="w-5 h-5 text-purple-500" />
                Subject Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {subjects?.subjects && subjects.subjects.length > 0 ? (
                  subjects.subjects.slice(0, 5).map(subject => (
                    <div key={subject.subject} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium capitalize text-slate-700">{subject.subject}</span>
                        <span className="text-sm text-slate-500">{subject.total_completions} completions</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <Progress value={subject.average_score} className="flex-1 h-2" />
                        <span className="text-sm font-semibold w-12">{subject.average_score}%</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-400 py-4">No subject data yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Students */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top Students
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topStudents?.top_students && topStudents.top_students.length > 0 ? (
                  topStudents.top_students.slice(0, 5).map((student, idx) => (
                    <div key={student.student_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-slate-400' : idx === 2 ? 'bg-amber-700' : 'bg-slate-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{student.name || 'Student'}</p>
                        <p className="text-xs text-slate-500">
                          {student.lessons_completed} lessons • {student.average_score}% avg
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-emerald-600">{student.total_points}</p>
                        <p className="text-xs text-slate-400">points</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-slate-400 py-4">No student data yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Game Analytics */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Gamepad2 className="w-5 h-5 text-pink-500" />
              Game Analytics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-4">
              {games?.games && Object.keys(games.games).length > 0 ? (
                Object.entries(games.games).map(([gameId, gameData]) => (
                  <div key={gameId} className="bg-slate-50 rounded-lg p-4 text-center">
                    <p className="font-medium text-slate-800 capitalize mb-2">
                      {gameId.replace(/-/g, ' ')}
                    </p>
                    <p className="text-2xl font-bold text-purple-600">{gameData.plays || 0}</p>
                    <p className="text-xs text-slate-500">plays</p>
                    <div className="mt-2 text-xs text-slate-500">
                      <span>Avg: {gameData.avg_score || 0}%</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="col-span-full text-center text-slate-400 py-4">No game data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </ProLayout>
  );
};

export default AdminAnalytics;
